import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FaRegFolderOpen } from 'react-icons/fa';
import { FiFolder } from 'react-icons/fi';
import { Button, Flex, Message, Spacer, Table, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { TimeTrackerHeader } from 'src/views/campaigns/time-tracker/time-tracker-header';
import { colorFromUuid } from 'src/views/campaigns/utils/utils';
import { formatTimeToUI } from 'src/views/time-tracker/utils/date/dateUtils';
import { CellWithExpandingIcon } from 'src/views/time-tracker/cell-with-expanding-icon';
import {
  generateCampaignTimeTracker,
  getCampaignTimeTracker,
  updateTimeTrackerTask,
} from 'src/store/entities/campaigns/campaigns';
import translations from 'src/internationalisation/translation-map.json';
import { getActivityVariance, getOperationVariance } from 'src/views/campaigns/time-tracker/time-tracker-utils';
import {
  getCampaignTimeTrackerOperationRows,
  isTimeTrackerSequenceChanged,
} from 'src/views/campaigns/utils/time-tracker-sequence';

import type { TRootState } from 'src/store/store-types';

type TTimeTracker = PropsFromRedux & {};

const TimeTracker = ({
  activeCampaign,
  isFetchingCampaignTimeTracker,
  isGeneratingTimeTracker,
  timeTracker,
  timeTrackerCampaignId,
  generateCampaignTimeTracker,
  getCampaignTimeTracker,
  updateTimeTrackerTask,
}: TTimeTracker) => {
  const { t } = useTranslation();
  const { campaignId } = activeCampaign || {};

  const [expandedOperationIds, setExpandedOperationIds] = useState<string[]>([]);

  const toggleExpandOperation = (operationId: string) => {
    return expandedOperationIds.includes(operationId)
      ? setExpandedOperationIds(expandedOperationIds.filter((id) => id !== operationId))
      : setExpandedOperationIds([...expandedOperationIds, operationId]);
  };

  useEffect(() => {
    if (campaignId) {
      getCampaignTimeTracker(campaignId);
    }
  }, [campaignId]);

  const rows: any[] = [];

  const operationRows = getCampaignTimeTrackerOperationRows(
    timeTrackerCampaignId === campaignId ? timeTracker : [],
    campaignId,
  );
  const hasTimeTracker = (operationRows?.length ?? 0) > 0;
  const hasLoadedTimeTracker = !!campaignId && timeTrackerCampaignId === campaignId && !isFetchingCampaignTimeTracker;
  const showTimeTrackerOutOfSyncWarning = hasTimeTracker && isTimeTrackerSequenceChanged(activeCampaign, operationRows);

  const onGenerateTimeTracker = () => {
    if (!campaignId || isGeneratingTimeTracker) {
      return;
    }

    generateCampaignTimeTracker({
      campaignId,
      sequence: activeCampaign?.operationSequence ?? [],
      force: showTimeTrackerOutOfSyncWarning,
    });
  };

  operationRows?.forEach(({ estimate, estimateIdx, operation, operationIdx }) => {
    const isExpandedOperation = expandedOperationIds.includes(operation.timeTrackerItemId);
    rows.push({
      estimatePosition: estimate.position,
      noDrag: true,
      cells: [
        {
          value: (
            <CellWithExpandingIcon
              cellContent={estimate.name}
              index={1}
              isBold={true}
              isBadge={true}
              badgeColor={colorFromUuid(estimate.campaignsEstimatesId, 90)}
              badgeTextColor={colorFromUuid(estimate.campaignsEstimatesId, 30)}
              textAlign="left"
            />
          ),
        },
        {
          value: operation?.section?.name,
        },
        {
          value: (
            <CellWithExpandingIcon
              cellContent={operation.name}
              index={0}
              icon={isExpandedOperation ? <FaRegFolderOpen /> : <FiFolder />}
              isBold={true}
              onClick={() => toggleExpandOperation(operation.timeTrackerItemId)}
              textAlign="left"
            />
          ),
        },
        {
          value: formatTimeToUI(operation.start),
        },
        {
          value: operation.afe,
        },
        {
          value: operation.estimate,
        },
        {
          value: operation.actual,
        },
        {
          value: getOperationVariance(operation),
        },
        {
          value: operation.plannedDepth,
        },
        {
          value: operation.actualDepth,
        },
      ],
    });
    if (isExpandedOperation) {
      operation.activities.forEach((activity: any, taskIdx: number) => {
        rows.push({
          noDrag: true,
          cells: [
            { value: '' },
            { value: '' },
            { value: activity.name },
            { value: formatTimeToUI(activity.start) },
            { value: activity.afe },
            { value: activity.estimate },
            {
              value: activity.actual ?? 0,
              type: 'NumberInput',
              allowEmpty: true,
              onChange: (e: any) =>
                updateTimeTrackerTask({
                  field: 'actual',
                  value: e.target.value,
                  campaignId,
                  taskId: activity.timeTrackerItemId,
                }),
            },
            { value: getActivityVariance(activity) },
            { value: activity.plannedDepth },
            {
              value: activity.actualDepth ?? 0,
              type: 'NumberInput',
              onChange: (e: any) =>
                updateTimeTrackerTask({
                  field: 'actualDepth',
                  value: e.target.value,
                  campaignId,
                  taskId: activity.timeTrackerItemId,
                  estimateIdx,
                  operationIdx,
                  taskIdx,
                }),
            },
          ],
        });
      });
    }
  });

  const table = {
    headers: [
      {
        cells: [
          { value: t(translations.estimate) },
          { value: t(translations.section) },
          { value: t(translations.operation) },
          { value: t(translations.timeTracker_startDate) },
          { value: 'Mean' },
          { value: 'P10' },
          { value: t(translations.timeTracker_actual) },
          { value: t(translations.timeTracker_variance) },
          { value: t(translations.timeTracker_plannedDepth) },
          { value: t(translations.timeTracker_actualDepth) },
        ],
      },
      {
        cells: [
          { value: '' },
          { value: '' },
          { value: '' },
          { value: '' },
          { value: t(translations.hours) },
          { value: t(translations.hours) },
          { value: t(translations.hours) },
          { value: t(translations.hours) },
          { value: 'm' }, // depth unit
          { value: 'm' },
        ],
      },
    ],
    rows,
  };

  return (
    <div>
      {hasTimeTracker ? (
        <>
          {showTimeTrackerOutOfSyncWarning && (
            <>
              <Spacer />
              <Message
                message={{
                  content: (
                    <Flex justifyContent="space-between" alignItems="center" gap="10px">
                      <span>{t(translations.campaigns_timeTrackerOutOfSyncWithOperationSequence)}</span>
                      <Button
                        label={t(translations.campaigns_generateTimeTrackerOverwriteTitle)}
                        onClick={onGenerateTimeTracker}
                        disabled={!hasLoadedTimeTracker || isGeneratingTimeTracker}
                        loading={isGeneratingTimeTracker}
                        small
                        colored
                      />
                    </Flex>
                  ),
                  type: 'Warning',
                  visible: true,
                  width: '100%',
                }}
              />
            </>
          )}
          <TimeTrackerHeader />
          <Spacer />
          <Table table={table} />
        </>
      ) : hasLoadedTimeTracker ? (
        <Flex alignItems="center" justifyContent="center" height="calc(100vh - 220px)">
          <Button
            label={t(translations.campaigns_generateTimeTracker)}
            onClick={onGenerateTimeTracker}
            disabled={!campaignId}
            loading={isGeneratingTimeTracker}
            colored
          />
        </Flex>
      ) : (
        <></>
      )}
    </div>
  );
};

const mapStateToProps = ({ entities }: TRootState) => {
  const { isFetchingCampaignTimeTracker, isGeneratingTimeTracker, list, timeTracker, timeTrackerCampaignId } =
    entities.campaigns;
  const activeCampaign = list.find((campaign) => campaign.active);
  return {
    activeCampaign,
    isFetchingCampaignTimeTracker,
    isGeneratingTimeTracker,
    timeTracker,
    timeTrackerCampaignId,
  };
};

const mapDispatchToProps = {
  generateCampaignTimeTracker,
  getCampaignTimeTracker,
  updateTimeTrackerTask,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(TimeTracker), {
  style: { position: 'absolute', top: '50px' },
});

export { Container as TimeTracker };
